'use client';

import React, { ReactNode, Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { recordActionTrailEvent } from './ActionTrail';

function pickActionTarget(el: Element | null): Element | null {
  let cur: Element | null = el;
  for (let i = 0; i < 6 && cur; i += 1) {
    if (
      cur.hasAttribute('data-action') ||
      cur.hasAttribute('data-testid') ||
      cur.hasAttribute('aria-label') ||
      cur.getAttribute('role') ||
      cur.tagName === 'A' ||
      cur.tagName === 'BUTTON' ||
      cur.tagName === 'INPUT' ||
      cur.tagName === 'TEXTAREA' ||
      cur.tagName === 'SELECT'
    ) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return el;
}

function safeAttr(el: Element, name: string): string | undefined {
  const v = el.getAttribute(name);
  if (!v) return undefined;
  const trimmed = v.trim();
  return trimmed.length ? trimmed : undefined;
}

function describeElement(el: Element): Record<string, unknown> {
  const tag = el.tagName.toLowerCase();
  const role = safeAttr(el, 'role');
  const testId = safeAttr(el, 'data-testid');
  const action = safeAttr(el, 'data-action');
  const ariaLabel = safeAttr(el, 'aria-label');
  const id = safeAttr(el, 'id');

  // Avoid capturing textContent/value for privacy. Prefer semantic attributes.
  const href = tag === 'a' ? safeAttr(el, 'href') : undefined;
  const name = safeAttr(el, 'name');
  const type = tag === 'input' ? safeAttr(el, 'type') : undefined;

  return {
    tag,
    role,
    testId,
    action,
    ariaLabel,
    id,
    href,
    name,
    inputType: type,
  };
}

function shouldTrackKeydown(ev: KeyboardEvent): boolean {
  if (ev.metaKey || ev.ctrlKey || ev.altKey) return true;
  return ev.key === 'Escape' || ev.key === 'Enter' || ev.key === 'Tab';
}

function ActionTrailSearchParamsTracker() {
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? '';

  useEffect(() => {
    recordActionTrailEvent({
      type: 'trail',
      name: 'init',
      data: { pathname, hasSearch: !!search },
      url: `${pathname}${search ? `?${search}` : ''}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    recordActionTrailEvent({
      type: 'nav',
      name: 'route-change',
      data: { pathname, hasSearch: !!search },
      url: `${pathname}${search ? `?${search}` : ''}`,
    });
  }, [pathname, search]);

  return null;
}

export function ActionTrailProvider({ children }: { children: ReactNode }) {

  useEffect(() => {
    const onClick = (ev: MouseEvent) => {
      const rawTarget = ev.target instanceof Element ? ev.target : null;
      const target = pickActionTarget(rawTarget);
      if (!target) return;

      recordActionTrailEvent({
        type: 'ui',
        name: 'click',
        data: {
          ...describeElement(target),
          button: ev.button,
          metaKey: ev.metaKey,
          ctrlKey: ev.ctrlKey,
          shiftKey: ev.shiftKey,
        },
      });
    };

    const onInput = (ev: Event) => {
      const rawTarget = ev.target instanceof Element ? ev.target : null;
      const target = pickActionTarget(rawTarget);
      if (!target) return;

      recordActionTrailEvent({
        type: 'ui',
        name: 'input',
        data: {
          ...describeElement(target),
          eventType: ev.type,
        },
      });
    };

    const onKeydown = (ev: KeyboardEvent) => {
      if (!shouldTrackKeydown(ev)) return;
      recordActionTrailEvent({
        type: 'ui',
        name: 'keydown',
        data: {
          key: ev.key,
          metaKey: ev.metaKey,
          ctrlKey: ev.ctrlKey,
          shiftKey: ev.shiftKey,
          altKey: ev.altKey,
        },
      });
    };

    window.addEventListener('click', onClick, { capture: true });
    window.addEventListener('input', onInput, { capture: true });
    window.addEventListener('change', onInput, { capture: true });
    window.addEventListener('keydown', onKeydown, { capture: true });
    return () => {
      window.removeEventListener('click', onClick, { capture: true } as any);
      window.removeEventListener('input', onInput, { capture: true } as any);
      window.removeEventListener('change', onInput, { capture: true } as any);
      window.removeEventListener('keydown', onKeydown, { capture: true } as any);
    };
  }, []);

  useEffect(() => {
    const onVaultBreadcrumb = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      const count = Array.isArray(detail?.breadcrumb) ? detail.breadcrumb.length : undefined;
      recordActionTrailEvent({ type: 'vault', name: 'breadcrumb-hydrated', data: { count } });
    };

    const onVaultTitle = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      const titleLen = typeof detail?.title === 'string' ? detail.title.length : undefined;
      recordActionTrailEvent({ type: 'vault', name: 'title-hydrated', data: { titleLen } });
    };

    const onVaultFilePath = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      const pathLen = typeof detail?.path === 'string' ? detail.path.length : undefined;
      recordActionTrailEvent({ type: 'vault', name: 'file-path', data: { pathLen } });
    };

    const onEditorState = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      const markdownLen = typeof detail?.markdown === 'string' ? detail.markdown.length : undefined;
      const originalLen = typeof detail?.originalMarkdown === 'string' ? detail.originalMarkdown.length : undefined;
      recordActionTrailEvent({
        type: 'vault',
        name: 'editor-state',
        data: { markdownLen, originalLen, changed: markdownLen !== originalLen },
      });
    };

    const onDrawerOpen = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      const anchor = detail?.anchor;
      const startLine = typeof anchor?.startLine === 'number' ? anchor.startLine : undefined;
      const endLine = typeof anchor?.endLine === 'number' ? anchor.endLine : undefined;
      recordActionTrailEvent({ type: 'comments', name: 'drawer-open', data: { startLine, endLine } });
    };

    const onCommentDeactivate = () => {
      recordActionTrailEvent({ type: 'comments', name: 'deactivate' });
    };

    window.addEventListener('vault:breadcrumb-hydrated', onVaultBreadcrumb as EventListener);
    window.addEventListener('vault:title-hydrated', onVaultTitle as EventListener);
    window.addEventListener('vault:file-path', onVaultFilePath as EventListener);
    document.addEventListener('vault:editor-state', onEditorState as EventListener);
    document.addEventListener('drawer:open', onDrawerOpen as EventListener);
    document.addEventListener('comment:deactivate', onCommentDeactivate as EventListener);

    return () => {
      window.removeEventListener('vault:breadcrumb-hydrated', onVaultBreadcrumb as EventListener);
      window.removeEventListener('vault:title-hydrated', onVaultTitle as EventListener);
      window.removeEventListener('vault:file-path', onVaultFilePath as EventListener);
      document.removeEventListener('vault:editor-state', onEditorState as EventListener);
      document.removeEventListener('drawer:open', onDrawerOpen as EventListener);
      document.removeEventListener('comment:deactivate', onCommentDeactivate as EventListener);
    };
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <ActionTrailSearchParamsTracker />
      </Suspense>
      {children}
    </>
  );
}
